import React, { useState,useEffect } from "react";
import { toast } from 'react-toastify';
import {format } from 'date-fns';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getDefaultReviewList,getSingleReviewTemplateDetail,searchHotelsReview } from "../../../../utils/reviewApi";
import { getSingleWidgetDetail } from "../../../../staticWidgets/utils/CommonWidgetApi";
import RatingStars from "../../../../components/RatingStars";
import ReviewPopup from "./ReviewPopup";
import BadgeWidget from "./BadgeWidget";
import FloatingWidget from "./FloatingWidget";

const CommonWidget = ({selectedLayout,isTemplateId,widgetId,reviewTabData}) => {
    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedReview, setSelectedReview] = useState(null);
    const [visibleGridCount, setVisibleGridCount] = useState(8);
    const [visibleListLayoutCount, setVisibleListLayoutCount] = useState(5);
    const [visibleMasonryLayoutCount, setVisibleMasonryLayoutCount] = useState(8);

    // Default values for the reviewTabData
    const {
        is_website_frontend,
        layout_is_full_width,
        business_name,
        layout_custom_width,
        style_background_color,
        header_is_widget_title,
        header_title,
        header_caption,
        setting_rating_format,
        style_widget_title_color,
        style_widget_title_fonts_title_size,
        style_widget_title_caption_color,
        style_widget_title_fonts_caption_size,
        style_header_background_color,
        header_is_show_header,
        header_write_reviews_button,
        header_source_icon,
        header_rating,
        header_number_of_reviews,
        style_header_text_color,
        style_header_write_review_background_color,
        style_header_write_review_text_color,
        header_write_review_button_type,
        style_rating_stars_color,
        layout_rows_on_mobile,
        layout_rows,
        style_load_more_button_color,
        style_load_more_button_text_color
    } = reviewTabData || {};

    // Map layout name to a specific class
    const layoutClassMap = {
        carousel_layout: 'upgadeify-carousel-style',
        list_layout: 'upgadeify-list-style',
        grid_layout: 'upgadeify-grid-style',
        masonry_layout: 'upgadeify-masonry-style',
        slider_layout: 'upgadeify-slider-style',
    };
    const layoutClass = layoutClassMap[selectedLayout] || '';

    //Helper to get slidesToShow from width
    const getSlidesToShow = (width) => {
        const w = Number(width);
        if (w >= 1024) return 3;
        if (w >= 768) return 2;
        return 1;
    };
    //State use for handle responsive logic
    const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow(layout_custom_width));

    //Update slidesToShow when window resizes or width changes
    useEffect(() => {
        const handleResize = () => {
        setSlidesToShow(getSlidesToShow(layout_custom_width));
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [layout_custom_width]);

    //Carousel Slider Settings
    const carouselSliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 1,
        arrows: true,
        autoplay: false,
        responsive: [
        {
            breakpoint: 768,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: true,
                dots: false,
            },
        },
        ],
    };

    //Slider Settings
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 3,
        arrows: false,
        autoplay: false,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: false,
                },
            },
        ],
    };

    // Fetching Review data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let result;
                if (isTemplateId) {
                    result = await getSingleReviewTemplateDetail(isTemplateId);
                } else if (widgetId) {
                    result = await getSingleWidgetDetail(widgetId);
                } else if (business_name) {
                    const payload = { business_name: business_name };
                    result = await searchHotelsReview(payload);
                } else {
                    let app_type = 'Hotels_Review';
                    result = await getDefaultReviewList(app_type);
                }
                setApiData(result); 
            } catch (error) {
                setApiData(null); 
                toast.warning(error.message);
            } finally {
                setLoading(false); 
            }
        };

        fetchData();
    }, [isTemplateId, widgetId, business_name]);

    //Handle Layout Rows and Columns
    const layoutColumns = 3;
    // Simple: detect if the screen is mobile once at render time
    const isMobile = window.innerWidth <= 768;
    const layoutRows = isMobile
        ? layout_rows_on_mobile
        : layout_rows;

    useEffect(() => {
        const newGridCount = layoutRows * layoutColumns;
        const newMasonryCount = layoutRows * layoutColumns;

        setVisibleGridCount(newGridCount);
        setVisibleListLayoutCount(layoutRows);
        setVisibleMasonryLayoutCount(newMasonryCount);
    }, [layoutRows, layoutColumns]);

    // Open popup when clicking "Read More"
    const handleReadMoreClick = (review) => {
        setSelectedReview(review);
    };

    // Close the popup
    const handleClosePopup = () => {
        setSelectedReview(null);
    };

    // Load More List Layout function
    const handleGridLoadMore = () => {
        setVisibleGridCount((prevCount) => prevCount + 8);
    };
    
    // Load List Layout More function
    const handleListLayoutLoadMore = () => {
        setVisibleListLayoutCount((prevCount) => prevCount + 5);
    };

    // Load Masonry Layout More function
    const handleMasonryLayoutLoadMore = () => {
        setVisibleMasonryLayoutCount((prevCount) => prevCount + 8);
    };

    //Handle dynamic styles based on layout
    const dynamicStyle =
        selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout'
            ? Number(layout_is_full_width) === 1
            ? {
                backgroundColor: style_background_color,
                margin: '0 auto',
            }
            : {
                backgroundColor: style_background_color,
                maxWidth: `${layout_custom_width}px`,
                margin: '0 auto',
                }
            : {};

    // Get grid layout style based on custom width
    const getGridLayoutStyle = () => {
        if (selectedLayout !== 'grid_layout') return {};

        if (layout_custom_width > 1280 && layout_custom_width < 1921) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(24%, 1fr))',
            
            };
        }

        if (layout_custom_width > 991 && layout_custom_width < 1280) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        
            };
        }

        if (layout_custom_width > 540 && layout_custom_width < 991) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(48%, 1fr))',
            
            };
        }

        if (layout_custom_width > 0 && layout_custom_width < 540) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(100%, 1fr))',
            
            };
        }

        // Default fallback
        return {
            gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        };
    };

    return (
        <div className={`upgadeify-review-template-widget upgadeify ${layoutClass} ${selectedReview ? 'carousel-popup-open' : ''} ${is_website_frontend}`} style={dynamicStyle}>
             {/* Loader and No Api Data Handling */}
            {loading ? (
                <div className="loader-container">
                    <div className="spinner"></div>
                    <p>Loading please wait...</p>
                </div>
            ) : apiData?.status === true ? (
            <>
                {/* Show Header Section Accordgin to condition*/}
                {selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout' && (
                    <>
                    {Number(header_is_widget_title) === 1 && (
                        <>
                            <div className="upgadeify-hotels-main-title" style={{ color: style_widget_title_color,fontSize: style_widget_title_fonts_title_size }}>{header_title || ""}</div>
                            <div className="upgadeify-hotels-main-caption" style={{ color: style_widget_title_caption_color,fontSize: style_widget_title_fonts_caption_size }}>{header_caption || ""}</div>
                        </>
                    )}
                    {Number(header_is_show_header) === 1 &&
                        (Number(header_source_icon) === 1 ||
                            Number(header_rating) === 1 ||
                            Number(header_number_of_reviews) === 1 ||
                            Number(header_write_reviews_button) === 1) && (
                            <div className="upgadeify-hotels-reviews-header" style={{ backgroundColor: style_header_background_color,}}>
                        
                                <div className='upgadeify-hotels-reviews-header-left'>
                                    {Number(header_source_icon) === 1 && (
                                    <div className='upgadeify-hotels-reviews-header-title'>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 123 36" className="injected-svg"><g fill="#EC2C45" clip-path="url(#a-281)"><path d="M7.574 5.614A3.433 3.433 0 0 1 11.01 9.05v4.822l6.87.363v-4.48A2.76 2.76 0 0 1 20.632 7c1.515 0 2.753 1.238 2.774 2.752V26.95a2.76 2.76 0 0 1-2.752 2.752 2.76 2.76 0 0 1-2.753-2.752v-5.505H11.01v5.505a3.433 3.433 0 0 1-3.436 3.435A3.433 3.433 0 0 1 4.14 26.95V9.049a3.433 3.433 0 0 1 3.435-3.435ZM3.755 7.492a3.941 3.941 0 0 0-.32 1.579v17.901c0 .939.32 1.814.875 2.518a3.456 3.456 0 0 1-2.24-3.222V10.436c0-1.259.682-2.347 1.685-2.944ZM17.22 22.15v4.822c0 .725.213 1.387.597 1.942a2.708 2.708 0 0 1-1.984-2.625V22.15h1.387Zm-2.07-.021v4.139c0 .725.213 1.387.597 1.941a2.708 2.708 0 0 1-1.984-2.624h.021V22.13h1.366ZM1.686 8.879a3.767 3.767 0 0 0-.32 1.557v15.832c0 .576.128 1.088.32 1.579A3.424 3.424 0 0 1 0 24.902V11.823c0-1.259.683-2.347 1.686-2.944Zm16.109-1.067a3.538 3.538 0 0 0-.598 1.941h-.021v3.734l-1.366-.064v-2.987c0-1.237.854-2.283 1.985-2.624Zm-2.07.683a3.374 3.374 0 0 0-.597 1.941h-.022v2.945l-1.365-.064v-2.198c0-1.238.853-2.283 1.984-2.624ZM44.401 23.75v-5.1h-7.425v5.1a.997.997 0 0 1-.981 1.003c-.555 0-1.024-.47-1.024-1.003V12.122a1.03 1.03 0 0 1 1.002-1.003c.555 0 1.003.47 1.003 1.003v4.736h7.425v-4.736c0-.534.448-1.003 1.003-1.003.555 0 1.003.47 1.003 1.003V23.75c0 .555-.448 1.003-1.003 1.003-.555.021-1.003-.448-1.003-1.003Zm11.373-3.968c0-1.835-1.067-3.478-3.03-3.478-1.942 0-3.03 1.643-3.03 3.478 0 1.856 1.088 3.499 3.03 3.499 1.963.021 3.03-1.643 3.03-3.5Zm-7.938 0c0-2.796 1.9-5.1 4.908-5.1 3.051 0 4.907 2.304 4.907 5.1 0 2.816-1.877 5.12-4.907 5.12-3.009 0-4.907-2.283-4.907-5.12Zm17.39-.704h5.953c-.043-1.28-.918-2.902-2.966-2.902-1.963.021-2.88 1.579-2.987 2.902Zm2.987-4.374c2.923 0 4.737 2.282 4.737 4.843 0 .555-.385.875-1.067.875h-6.636c.15 1.643 1.344 3.03 3.307 3.03.832 0 1.686-.257 2.411-.79.107-.085.235-.107.406-.107a.72.72 0 0 1 .725.726.71.71 0 0 1-.32.597c-.96.704-2.112 1.046-3.414 1.046-2.902 0-5.035-2.049-5.035-5.121 0-2.838 2.048-5.1 4.886-5.1Zm5.889 9.153v-11.82c0-.491.405-.897.896-.897.49 0 .917.406.917.896v11.82a.924.924 0 0 1-.917.918.906.906 0 0 1-.896-.917Zm2.987-.683a.775.775 0 0 1 1.237-.64 4.617 4.617 0 0 0 2.731.918c1.366 0 2.091-.555 2.091-1.387 0-.96-1.11-1.28-2.347-1.58-1.642-.383-3.584-.831-3.584-2.922 0-1.558 1.323-2.88 3.712-2.88 1.345 0 2.412.405 3.244 1.002.17.15.32.341.32.597a.756.756 0 0 1-.747.747.673.673 0 0 1-.405-.15c-.62-.469-1.473-.746-2.39-.746-1.238 0-2.006.49-2.006 1.259 0 .853 1.046 1.13 2.262 1.408 1.685.384 3.712.854 3.712 3.073 0 1.664-1.365 2.987-3.926 2.987-1.344 0-2.56-.342-3.541-1.088-.235-.086-.363-.342-.363-.598Zm10.177-.811c.683 0 1.26.576 1.26 1.26 0 .682-.577 1.258-1.26 1.258a1.275 1.275 0 0 1-1.259-1.259c.022-.683.577-1.259 1.26-1.259Zm18.627-2.581c0-1.835-1.067-3.478-3.03-3.478-1.941 0-3.03 1.643-3.03 3.478 0 1.856 1.089 3.499 3.03 3.499 1.963.021 3.03-1.643 3.03-3.5Zm-7.937 0c0-2.796 1.899-5.1 4.907-5.1 3.052 0 4.908 2.304 4.908 5.1 0 2.816-1.878 5.12-4.908 5.12-3.008 0-4.907-2.283-4.907-5.12Zm24.836-2.155v6.23c0 .49-.427.896-.918.896a.901.901 0 0 1-.896-.896V18.18c0-1.13-.512-1.664-1.686-1.664-.981 0-1.962 1.11-2.411 1.814v5.547a.906.906 0 0 1-.917.896c-.491 0-.917-.405-.917-.896v-5.675c0-1.131-.491-1.665-1.707-1.665-.939 0-1.899 1.174-2.39 1.878v5.462a.916.916 0 0 1-.896.896.907.907 0 0 1-.918-.896V15.75c0-.512.406-.918.875-.918.512 0 .918.406.918.918v.555c.405-.598 1.685-1.622 3.136-1.622 1.472 0 2.347.768 2.646 1.814.555-.918 1.877-1.814 3.307-1.814 1.835.021 2.774 1.003 2.774 2.945Z"></path><path d="M93.987 14.704c1.387 0 2.347.448 3.094 1.13.15.15.278.384.278.598 0 .47-.363.832-.811.832a.966.966 0 0 1-.555-.192c-.555-.534-1.195-.747-1.963-.747-1.92 0-3.136 1.472-3.136 3.478 0 2.006 1.237 3.435 3.136 3.5 1.302.042 2.582-.897 3.627-2.54.128-.213.32-.576.47-.79.298-.383.512-.191.682-.02.32.34.32 1.13.107 1.514-.597 1.088-1.088 1.6-1.643 2.155-.79.768-1.941 1.28-3.35 1.28-2.944 0-4.95-2.197-4.95-5.12.043-2.902 2.07-5.079 5.014-5.079Zm-29.359 8.47c0 .341-.981 1.11-1.685 1.451-.406.192-.811.277-1.387.277-1.536 0-2.326-.874-2.326-2.39v-5.974h-.917a.824.824 0 0 1-.811-.81c0-.427.384-.79.81-.79h.918v-1.856a.906.906 0 1 1 1.814 0v1.856h1.28c.448 0 .81.363.81.79a.81.81 0 0 1-.81.81h-1.28v5.57c0 .682.32 1.194.939 1.194 1.11 0 1.984-1.728 2.475-1.728.469 0 .17 1.152.17 1.6Z"></path></g><defs><clipPath id="a-281"><path fill="#fff" d="M0 0h122.792v36H0z"></path></clipPath></defs></svg>
                                        <div className='upgadeify-hotels-reviews-header-title-text' style={{color: style_header_text_color,}}>Reviews</div>
                                    </div>
                                    )}
                                    <div className='upgadeify-hotels-reviews-header-rating'>
                                        {Number(header_rating) === 1 && (
                                        <>
                                            <div className="upgadeify-hotels-reviews-header-rating-text" style={{color: style_header_text_color,}}>{setting_rating_format === '4.9' ? Number(apiData.data.average_rating).toFixed(1) : Number(apiData.data.average_rating).toFixed(2) }</div>
                                            <div className="upgadeify-hotels-reviews-item-rating">
                                                <RatingStars average_rating={apiData.data.average_rating} newColor={style_rating_stars_color} />
                                            </div>
                                        </>
                                        )}
                                        {Number(header_number_of_reviews) === 1 && (
                                            <span className="upgadeify-hotels-reviews-header-number-of-reviews-text">({apiData.data.total_reviews})</span>
                                        )}
                                    </div>
                                </div>
                                {Number(header_write_reviews_button) === 1 && (
                                    <div className='upgadeify-hotels-reviews-header-right'>
                                        <a
                                            href={apiData?.data?.google_url?.startsWith('http') ? apiData.data.google_url : `https://${apiData.data.google_url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className='upgadeify-hotels-reviews-header-button'
                                            style={{color: style_header_write_review_text_color,backgroundColor: style_header_write_review_background_color,fontSize: header_write_review_button_type}}>Review us on Hotels
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/*If Carousel Layout Type*/}
                {selectedLayout === 'carousel_layout' && (
                    <Slider {...carouselSliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                        ))}
                    </Slider>
                )}

                {/*If List Layout Type*/}
                {selectedLayout === 'list_layout' && (
                    <>
                        <div className='upgadeify-list_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleListLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {/* Load More Button  */}
                        {visibleListLayoutCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleListLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Grid Layout Type*/}
                {selectedLayout === 'grid_layout' && (
                    <>
                        <div className='upgadeify-grid-layout' style={getGridLayoutStyle()}>
                            {apiData.data.review_list.slice(0, visibleGridCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {visibleGridCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleGridLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Masonry Layout Type*/}
                {selectedLayout === 'masonry_layout' && (
                    <>
                        <div className='upgadeify-masonry_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleMasonryLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                            
                        </div>
                            {/* Load More Button  */}
                            {visibleMasonryLayoutCount < apiData.data.review_list.length && (
                                <div className="load-more-button">
                                    <button onClick={handleMasonryLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                                </div>
                            )}
                    </>
                )}

                {/*If Slider Layout Type*/}
                {selectedLayout === 'slider_layout' && (
                    <Slider {...sliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData} />
                        ))}
                    </Slider>
                )}

                {/*If Badge Layout Type*/}
                {selectedLayout === 'badge_layout' && (
                    <BadgeWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*If Floating Badge Layout Type*/}
                {selectedLayout === 'floating_badge_layout' && (
                    <FloatingWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*Common Popup */}
                {selectedReview && <ReviewPopup review={selectedReview} onClose={handleClosePopup} />}
            </>
            ) : (
                <div className="no-inner-result-found">
                    <p>No Hotels reviews found please try.</p>
                </div>
            )}
        </div >
    );
};

const ReviewLoopItem = ({ review, handleReadMoreClick, reviewTabData }) => (
    <>
        <div className="upgadeify-hotels-reviews">
            <div className='upgadeify-hotels-reviews-item' style={{ padding: `${reviewTabData.layout_item_spacing}px`, backgroundColor: reviewTabData.style_review_background_color,}}>
                <div className='upgadeify-hotels-reviews-head'>
                    {(Number(reviewTabData.review_is_show_reviewer_picture) === 1 || Number(reviewTabData.review_is_show_verified_badge) === 1) && (
                        <div className="upgadeify-hotels-reviews-item-image">
                            {review.image ? (
                                <img src={review.image} alt="Review" />
                            ) : (
                                <div className="fallback-avatar">
                                    {review.name.charAt(0)}
                                </div>
                            )}
                           
                        </div>
                    )}
                    <div className='upgadeify-hotels-reviews-item-name'>
                        {Number(reviewTabData.review_is_show_reviewer_name) === 1 && (
                        Number(reviewTabData.setting_is_enable_external_links) === 1 && review.author_url ? ( 
                                <a
                                    href={review.author_url} 
                                    target={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "_blank" : "_self"}
                                    rel={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "noopener noreferrer" : undefined}
                                    style={{ color: reviewTabData.style_review_reviewer_name_color }}
                                >
                                    <span className="upgadeify-hotels-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>{review.name}</span>
                                </a>
                            ) : (
                                <span className="upgadeify-hotels-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>
                                    {review.name}
                                </span>
                            )
                        )}

                        {Number(reviewTabData.review_is_show_verified_badge) === 1 && (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14" className="review-verified-badge-icon" aria-hidden="true">
                                <path fill={reviewTabData.style_review_verified_badge_color} d="M6.757.236a.35.35 0 0 1 .486 0l1.106 1.07a.35.35 0 0 0 .329.089l1.493-.375a.35.35 0 0 1 .422.244l.422 1.48a.35.35 0 0 0 .24.24l1.481.423a.35.35 0 0 1 .244.422l-.375 1.493a.35.35 0 0 0 .088.329l1.071 1.106a.35.35 0 0 1 0 .486l-1.07 1.106a.35.35 0 0 0-.089.329l.375 1.493a.35.35 0 0 1-.244.422l-1.48.422a.35.35 0 0 0-.24.24l-.423 1.481a.35.35 0 0 1-.422.244l-1.493-.375a.35.35 0 0 0-.329.088l-1.106 1.071a.35.35 0 0 1-.486 0l-1.106-1.07a.35.35 0 0 0-.329-.089l-1.493.375a.35.35 0 0 1-.422-.244l-.422-1.48a.35.35 0 0 0-.24-.24l-1.481-.423a.35.35 0 0 1-.244-.422l.375-1.493a.35.35 0 0 0-.088-.329L.236 7.243a.35.35 0 0 1 0-.486l1.07-1.106a.35.35 0 0 0 .089-.329L1.02 3.829a.35.35 0 0 1 .244-.422l1.48-.422a.35.35 0 0 0 .24-.24l.423-1.481a.35.35 0 0 1 .422-.244l1.493.375a.35.35 0 0 0 .329-.088L6.757.236Z"></path><path fill="#fff" fill-rule="evenodd" d="M9.065 4.85a.644.644 0 0 1 .899 0 .615.615 0 0 1 .053.823l-.053.059L6.48 9.15a.645.645 0 0 1-.84.052l-.06-.052-1.66-1.527a.616.616 0 0 1 0-.882.645.645 0 0 1 .84-.052l.06.052 1.21 1.086 3.034-2.978Z" clip-rule="evenodd"></path>
                            </svg>
                        )}

                        <span className="upgadeify-hotels-reviews-item-date">
                            {Number(reviewTabData.review_is_show_date) === 1 && (
                                <span style={{color: reviewTabData.style_review_date_color,}}>{review.review_date ? format(new Date(review.review_date), 'yyyy-MM-dd') : ''}</span>
                            )}
                        </span>
                    </div>
                </div>
                <div className="upgadeify-hotels-reviews-item-content">
                   {Number(reviewTabData.review_is_show_rating) === 1 && (
                        <span className="upgadeify-hotels-reviews-item-rating" style={{ fontSize: reviewTabData.style_review_rating_size }}>
                            <RatingStars average_rating={review.rating} newColor={reviewTabData.style_rating_stars_color}/>
                        </span>
                    )}
                    <div className="upgadeify-hotels-reviews-item-text" style={{color: `${reviewTabData.style_review_text_color}`,fontSize: reviewTabData.style_review_text_font_size}}>
                        {review.description.length > 50
                        ? `${review.description.slice(0, 50)}...`
                        : review.description}
                    </div>
                    {review.description.length > 50 && (
                        <div className='upgadeify-hotels-reviews-readmore' onClick={() => handleReadMoreClick(review)} style={{color: reviewTabData.style_read_more_color,fontSize: reviewTabData.style_review_text_font_size}}>
                                Read more
                        </div>
                    )}

                    {Number(reviewTabData.review_is_show_review_source) === 1 && (  
                        <div className='upgadeify-hotels-reviews-item-image-badge'>
                           <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 25" className="injected-svg"><circle cx="12" cy="12.5" r="10" fill="#EC2C45"></circle><path fill="#fff" d="M10.236 7c.811 0 1.468.682 1.468 1.525v2.142l2.935.16V8.839c0-.673.529-1.222 1.176-1.222.647 0 1.176.55 1.185 1.222v7.637c0 .672-.529 1.222-1.176 1.222-.647 0-1.176-.55-1.176-1.222V14.03h-2.944v2.445c0 .843-.657 1.525-1.468 1.525-.811 0-1.468-.682-1.468-1.525v-7.95c0-.843.657-1.525 1.468-1.525Zm-1.632.834a1.81 1.81 0 0 0-.136.7v7.95c0 .417.136.805.373 1.118a1.533 1.533 0 0 1-.957-1.43v-7.03c0-.56.292-1.043.72-1.308Zm5.752 6.509v2.141c0 .322.092.616.256.862a1.196 1.196 0 0 1-.848-1.165v-1.838h.592Zm-.884-.01v1.838c0 .323.091.616.255.863a1.196 1.196 0 0 1-.847-1.166h.009v-1.535h.583ZM7.72 8.45a1.73 1.73 0 0 0-.137.691v7.03c0 .256.055.484.137.701A1.533 1.533 0 0 1 7 15.565V9.757c0-.559.292-1.042.72-1.307Zm6.883-.474c-.155.246-.256.54-.256.862h-.009v1.658l-.583-.028V9.14c0-.55.364-1.014.848-1.165Zm-.885.303a1.54 1.54 0 0 0-.255.862h-.009v1.308l-.583-.029v-.976c0-.55.364-1.013.847-1.165Z"></path></svg>
                            Posted On   
                            <> <a href={`https://www.hotels.com`} target="blank">Hotels</a></> 
                        </div> 
                    )} 
                </div>
            </div>
        </div>
    </>
);

export default CommonWidget;
